import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { Response } from 'lib-shared-legacy-types';
import { Company, User } from 'lib-shared-types-api';
import { getHighestUserRole, USER_ROLE_ATTRIBUTES } from 'lib-shared-user-role';
import { destroy } from 'redux-form';

import { notificationActions } from 'components/notifications/actions/notification';
import { createCompany } from 'components/shared/companyModal/api/company';
import { getRequestID } from 'components/shared/util/request';
import { dataUriToFile } from 'utils/file/dataUriToFile';
import { pubSub } from 'utils/pubSub';

import {
  addUser,
  getPresigned,
  getUser,
  updateUser,
  uploadAvatar,
  validateEmail,
  validatePhoneNumber,
} from './userModal.api';
import { UserModalCompanyCreatePayload } from './userModal.types';
import {
  AddOrUpdateUserError,
  AddUserPayload,
  AddUserResponse,
  GetUserPayload,
  GetUserResponse,
  ReadCompaniesResponse,
  UpdateUserPayload,
  UpdateUserResponse,
  ValidateEmailResponse,
  ValidatePhoneNumberResponse,
} from './userModalApi.types';

export const userModalFormName = 'userModalForm';

const prefix = 'userModal';

export const userModalActions = {
  show: createAction<{
    user: Partial<User>;
  }>(`${prefix}/show`),
  hide: createAction(`${prefix}/hide`),

  user: {
    get: createAsyncThunk<GetUserResponse, GetUserPayload>(
      `${prefix}/user/get`,
      getUser,
    ),

    add: createAsyncThunk<AddUserResponse, AddUserPayload>(
      `${prefix}/user/add`,
      async (user, { dispatch, rejectWithValue }) => {
        try {
          let userWithAvatar: AddUserPayload | null = null;

          if (user.avatar_url) {
            const file = await dataUriToFile(user.avatar_url, 'avatar');
            const presignedResponse = await getPresigned({ file });
            await uploadAvatar({
              file,
              upload_url: presignedResponse.data.upload_url,
            });

            userWithAvatar = {
              ...user,
              avatar_url: presignedResponse.data.download_url,
            };
          }

          const addUserResp = await addUser(
            userWithAvatar ? userWithAvatar : user,
          );
          const roleName = addUserResp.data.roles
            ? USER_ROLE_ATTRIBUTES[getHighestUserRole(addUserResp.data.roles)]
                .prettyName
            : 'User';
          const notificationID = getRequestID();

          dispatch(userModalActions.hide());
          dispatch(destroy(userModalFormName));
          pubSub.publish('userModalUserAdded', user);
          dispatch(
            notificationActions.notificationAdd({
              id: notificationID,
              title: 'User saved',
              text: `New ${roleName} added`,
              status: 'success',
              type: 'userCreated',
              icon: 'done',
              time: 5000,
              rightAction: {
                text: 'Dismiss',
                action: () => {
                  dispatch(
                    notificationActions.notificationRemoveById({
                      id: notificationID,
                    }),
                  );
                },
              },
              leftAction: {
                text: `Go to new user`,
                action: {
                  navigate: `/contacts/user/${addUserResp.data.id}`,
                },
              },
            }),
          );

          return addUserResp;
        } catch (error: unknown) {
          if (!error) {
            dispatch(
              notificationActions.notificationAdd({
                title: 'Error',
                text: `An error occurred while adding the new user`,
                status: 'danger',
                time: 5000,
                icon: 'exclamation',
              }),
            );
          }

          return rejectWithValue(
            (error as AxiosError<AddOrUpdateUserError>)?.response?.data,
          );
        }
      },
    ),

    update: createAsyncThunk<UpdateUserResponse, UpdateUserPayload>(
      `${prefix}/user/update`,
      async (user, { dispatch, rejectWithValue }) => {
        try {
          let userWithAvatar: UpdateUserPayload | null = null;

          if (user.patch.updates?.avatar_url) {
            const file = await dataUriToFile(
              user.patch.updates?.avatar_url,
              'avatar',
            );
            const presignedResponse = await getPresigned({ file });
            await uploadAvatar({
              file,
              upload_url: presignedResponse.data.upload_url,
            });

            userWithAvatar = {
              ...user,
              patch: {
                ...user.patch,
                updates: {
                  ...user.patch.updates,
                  avatar_url: presignedResponse.data.download_url,
                },
              },
            };
          }

          const updatedUser = await updateUser(
            userWithAvatar ? userWithAvatar : user,
          );

          pubSub.publish('userModalUserUpdated', updatedUser.data);
          dispatch(userModalActions.hide());
          dispatch(destroy(userModalFormName));
          dispatch(
            notificationActions.notificationAdd({
              title: 'User updated',
              text: `The user is updated`,
              status: 'success',
              time: 5000,
              icon: 'done',
            }),
          );
          return updatedUser;
        } catch (error: unknown) {
          if (!error) {
            dispatch(
              notificationActions.notificationAdd({
                title: 'Error',
                text: `An error occurred while updating the user`,
                status: 'danger',
                time: 5000,
                icon: 'exclamation',
              }),
            );
          }

          return rejectWithValue(
            (error as AxiosError<AddOrUpdateUserError>)?.response?.data,
          );
        }
      },
    ),
  },

  validation: {
    email: createAsyncThunk<ValidateEmailResponse['data'], string>(
      `${prefix}/validation/email`,
      async (email, { rejectWithValue }) => {
        const { data } = await validateEmail(email);
        if (data.available) {
          return data;
        }
        return rejectWithValue(data);
      },
    ),
    phoneNumber: createAsyncThunk<ValidatePhoneNumberResponse['data'], string>(
      `${prefix}/validation/phone-number`,
      async (phoneNumber, { rejectWithValue }) => {
        const { data } = await validatePhoneNumber(phoneNumber);
        if (data.available) {
          return data;
        }
        return rejectWithValue(data);
      },
    ),
  },

  companies: {
    // TODO [TEAMS-6857]: When RTK Q is ready, `fulfilled` and `rejected`
    // should be removed and `get` should be converted to `createAsyncThunk`
    // and "companies select" in "userModal" should be paginated/infinite scrolled
    get: createAction(`${prefix}/companies/get`),
    fulfilled: createAction<ReadCompaniesResponse>(
      `${prefix}/companies/fulfilled`,
    ),
    rejected: createAction<Error>(`${prefix}/companies/rejected`),

    add: createAsyncThunk(
      `${prefix}/addCompany`,
      async (payload: UserModalCompanyCreatePayload) => {
        const { data } = await (createCompany(payload) as Promise<
          Response<Company>
        >);
        return { company: data };
      },
    ),
  },
};
